import { ApiHelper } from '../apiHelper';
import { HttpHeaders } from '@angular/common/http';
import { ConstantMan } from '../constantMan';
import { BaseDAO } from './baseDAO';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { Moment } from 'moment';
import * as moment from 'moment';
import { SignedResourceResponse } from './uploadDAO';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';
import { WaterBodyTimezone } from 'anchor-shared/lib/model/interface/waterBodyTimezone';

@Injectable()
export class VesselDAO extends BaseDAO<Vessel> {
  static PATHS = {
    BY_CAPTAIN: '/captain',
    BY_BOAT_OWNER: '/boat-owner',
    CHARTER: '/charter',
    UPDATE_WATER_BODIES: '/updateVesselWaterBodies',
    UPDATE_CAPTAINS: '/updateVesselCaptains',
    UPDATE_INSURANCE: '/updateVesselInsurance',
    SIGNUP: '/sign-up',
    SEND_APPROVAL_REQUEST: '/send-approval-request',
    SET_STATUS: '/set-status',
    SET_ACTIVE: '/set-active',
    PUBLISH_LIST_PRICE: '/publish-list-price',
    REQUEST_EDITS: '/request/vessel/edits',
    BUYFLOW: '/buyflow',
    LOG_SAVE: '/log-boat-owner-save-event',
    GET_CALENDAR_OPTIONS: '/get-calendar-options',
  };

  constructor(public readonly apiHelper: ApiHelper) {
    super(apiHelper, ConstantMan.API.RESOURCE.VESSEL);
  }

  public getCalendarOptions(vesselId: number): Observable<any[]> {
    let url: string = `${this.apiHelper.getServiceName()}${this.getResource()}${
      VesselDAO.PATHS.GET_CALENDAR_OPTIONS
    }/${vesselId}`;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    return this.apiHelper.apiCall<any[]>(url, method, null, data, headers);
  }

  public createVessel(captainId?: number): Observable<Vessel> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      VesselDAO.PATHS.BY_BOAT_OWNER;
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    let httpRequest: Observable<Vessel> = this.apiHelper.apiCall<Vessel>(
      url,
      method,
      null,
      null,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (vessel: Vessel) => {
          observer.next(vessel);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public setStatus(vesselId: number, status: string): Observable<Vessel> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      VesselDAO.PATHS.SET_STATUS +
      '/' +
      vesselId;
    let data = { status: status };
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    let httpRequest: Observable<Vessel> = this.apiHelper.apiCall<Vessel>(
      url,
      method,
      null,
      data,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (res: any) => {
          observer.next(res);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public setActive(vesselId: number, active: boolean): Observable<Vessel> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      VesselDAO.PATHS.SET_ACTIVE +
      '/' +
      vesselId;
    const data = { active: active };
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    const httpRequest: Observable<Vessel> = this.apiHelper.apiCall<Vessel>(
      url,
      method,
      null,
      data,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (res: any) => {
          observer.next(res);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public publishListPrice(
    vesselId: number,
    halfDay: number,
    fullDay: number,
    hourly: number,
  ): Observable<boolean> {
    const url =
      this.apiHelper.getServiceName() +
      this.getResource() +
      VesselDAO.PATHS.PUBLISH_LIST_PRICE +
      '/' +
      vesselId;
    const data = {
      halfDayListPrice: halfDay,
      fullDayListPrice: fullDay,
      additionalHoursListPrice: hourly,
    };
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    const httpRequest: Observable<boolean> = this.apiHelper.apiCall<boolean>(
      url,
      method,
      null,
      data,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (res: any) => {
          observer.next(res);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public requestEdits(vesselId: number, edits: string): Observable<Vessel> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/' +
      vesselId +
      VesselDAO.PATHS.REQUEST_EDITS;
    let data = { edits: edits };
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    let httpRequest: Observable<Vessel> = this.apiHelper.apiCall<Vessel>(
      url,
      method,
      null,
      data,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (res: any) => {
          observer.next(res);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public createForSignup(data: Vessel): Observable<Vessel> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      VesselDAO.PATHS.SIGNUP;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    data.status = ConstantMan.VESSEL_STATUSES.DRAFT;

    const httpRequest: Observable<Vessel> = this.apiHelper.apiCall<Vessel>(
      url,
      method,
      null,
      data,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (vessel: Vessel) => {
          observer.next(vessel);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public createVesselAdmin(data: any): Observable<Vessel> {
    const url: string = this.apiHelper.getServiceName() + this.getResource();
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    const httpRequest: Observable<Vessel> = this.apiHelper.apiCall<Vessel>(
      url,
      method,
      null,
      data,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (vessel: Vessel) => {
          observer.next(vessel);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public sendApprovalRequest(data: any): Observable<Vessel> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      VesselDAO.PATHS.SEND_APPROVAL_REQUEST;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    const httpRequest: Observable<Vessel> = this.apiHelper.apiCall<Vessel>(
      url,
      method,
      null,
      data,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (vessel: Vessel) => {
          observer.next(vessel);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public updateVesselWaterBodies(
    vesselId: number,
    ids: number[],
  ): Observable<any> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      VesselDAO.PATHS.UPDATE_WATER_BODIES;
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let data = {
      waterBodyIds: ids,
      vesselId: vesselId,
    };

    const httpRequest: Observable<Vessel> = this.apiHelper.apiCall<Vessel>(
      url,
      method,
      null,
      data,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (vessel: Vessel) => {
          observer.next();
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public updateVesselCaptains(
    vesselId: number,
    ids: number[],
  ): Observable<any> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      VesselDAO.PATHS.UPDATE_CAPTAINS;
    let method = 'POST';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let data = {
      captainIds: ids,
      vesselId: vesselId,
    };

    const httpRequest: Observable<Vessel> = this.apiHelper.apiCall<Vessel>(
      url,
      method,
      null,
      data,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (vessel: Vessel) => {
          observer.next();
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public getVessels(captainId?: number): any {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      VesselDAO.PATHS.BY_BOAT_OWNER;
    if (captainId) {
      url += '/' + captainId;
    } else {
      url += '/all';
    }
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let httpRequest: Observable<Vessel[]> = this.apiHelper.apiCall<Vessel[]>(
      url,
      method,
      null,
      null,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (vessels: Vessel[]) => {
          observer.next(vessels);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public loadVesselsByLosOrStatus(param: string): Observable<Vessel[]> {
    let url: string = this.apiHelper.getServiceName() + this.getResource();

    if (param === ConstantMan.VESSEL_QUERY_PARAMS.LOS.CHARTER) {
      url += `?los=${param}`;
    } else if (param === ConstantMan.VESSEL_QUERY_PARAMS.STATUS.UNAPPROVED) {
      url += `?status=${param}`;
    }

    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<Vessel[]>(url, method, null, null, headers);
  }

  public loadForAdminGrid(param: string): Observable<Vessel[]> {
    let url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/admin/grid';
    if (param === ConstantMan.VESSEL_QUERY_PARAMS.LOS.CHARTER) {
      url += `?los=${param}`;
    } else if (param === ConstantMan.VESSEL_QUERY_PARAMS.STATUS.UNAPPROVED) {
      url += `?status=${param}`;
    }
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    const httpRequest: Observable<Vessel[]> = this.apiHelper.apiCall<Vessel[]>(
      url,
      method,
      null,
      data,
      headers,
    );

    return httpRequest;
  }

  public loadForCharterPages(): Observable<any[]> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/charter-page/charter-vessels';
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    const httpRequest: Observable<Vessel[]> = this.apiHelper.apiCall<Vessel[]>(
      url,
      method,
      null,
      data,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (res: any) => {
          observer.next(res);
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  public loadByOwnerId(ownerId?: number): Observable<Vessel[]> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      VesselDAO.PATHS.BY_BOAT_OWNER;
    if (ownerId) {
      url += '/' + ownerId;
    }

    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<Vessel[]>(url, method, null, null, headers);
  }

  public loadFeaturedVessels(): Observable<Vessel[]> {
    let url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/featured';
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<Vessel[]>(url, method, null, null, headers);
  }

  public getFeaturedVessels(): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/featured';
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    return this.apiHelper.apiCall<Vessel>(url, method, null, data, headers);
  }

  public getOwnerVesselsLocal(ownerId?: number): Observable<Vessel[]> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      VesselDAO.PATHS.BY_BOAT_OWNER;
    let method = 'GET';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<Vessel[]>(url, method, null, null, headers);
  }

  public getActiveCharterVesselsLocal(): Observable<Vessel[]> {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/charter-page/charter-vessels';
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    return this.apiHelper.apiCall<Vessel[]>(url, method, null, data, headers);
  }

  public loadByIdForBuyflow(vesselId: number): Observable<Vessel> {
    let url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/' + vesselId;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<Vessel>(url, method, null, null, headers);
  }

  public getFullUnavailables(vesselId: number): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/charter-vessel/unavailability/' +
      vesselId;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      todaysDate: moment().format('x'),
    };
    const httpRequest: Observable<Vessel> = this.apiHelper.apiCall<Vessel>(
      url,
      method,
      null,
      data,
      headers,
    );

    return httpRequest;
  }

  public getByVesselId(
    vesselId: number,
    requestedDate?: Moment,
  ): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/buyflow/' +
      vesselId;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    let momentFormat = 'YYYY-MM-DD';

    let formattedDate;
    if (requestedDate) {
      formattedDate = requestedDate.format(momentFormat);
    }

    const data = {
      requestedDate: formattedDate,
    };
    return this.apiHelper.apiCall<Vessel>(url, method, null, data, headers);
  }

  public getVesselNotes(vesselId: number): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/notes/' +
      vesselId;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    return this.apiHelper.apiCall<Vessel>(url, method, null, data, headers);
  }

  public getByVesselIdCustomer(vesselId: number): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() + this.getResource() + '/' + vesselId;
    const method = 'GET';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {};
    return this.apiHelper.apiCall<Vessel>(url, method, null, data, headers);
  }

  public setPrimaryPhoto(
    vesselId: number,
    primaryPhoto: string,
  ): Observable<any> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/primary-photo/' +
      vesselId;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      primaryPhotoAbsPath: primaryPhoto,
    };
    return this.apiHelper.apiCall<Vessel>(url, method, null, data, headers);
  }

  logSaveFromBoatOwner(
    onTab: string,
    vessel: Vessel,
  ): Observable<{ message: string }> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      VesselDAO.PATHS.LOG_SAVE;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      eventDescription: `Tab: ${onTab} - Vessel ID: ${
        vessel.id
      } - Date: ${Date.now()}`,
      eventAncillary: { id: vessel.id, ownerId: vessel.ownerId },
    };
    return this.apiHelper.apiCall<{ message: string }>(
      url,
      method,
      null,
      data,
      headers,
    );
  }

  public timezoneById(id: number): Observable<WaterBodyTimezone> {
    const url = `${this.apiHelper.getServiceName()}${this.getResource()}/timezone/${id}`;
    const method = 'GET';
    const headers = this.apiHelper.getDefaultHeader();
    return this.apiHelper.apiCall<WaterBodyTimezone>(
      url,
      method,
      null,
      null,
      headers,
    );
  }

  updateVesselInsurance(vesselId: number, insurancePath: string) {
    let url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      VesselDAO.PATHS.UPDATE_INSURANCE +
      '/' +
      vesselId;
    let method = 'PUT';
    let headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    let data = {
      insurancePhotoAbsPath: insurancePath,
      vesselId: vesselId,
    };

    const httpRequest: Observable<Vessel> = this.apiHelper.apiCall<Vessel>(
      url,
      method,
      null,
      data,
      headers,
    );

    return Observable.create((observer) => {
      httpRequest.subscribe(
        (vessel: Vessel) => {
          observer.next();
          observer.complete();
        },
        (error) => {
          observer.error(error);
        },
      );
    });
  }

  GetSignedUrlForVesselInsurance(
    vesselId: string | number,
    resourceKey: string,
  ): Observable<SignedResourceResponse> {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/vessel/signed-url/' +
      vesselId;
    const method = 'PUT';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();
    const data = {
      key: resourceKey,
    };
    return this.apiHelper.apiCall<SignedResourceResponse>(
      url,
      method,
      null,
      data,
      headers,
    );
  }

  updateFromPartial(vesselId: number, vessel: Partial<Vessel>) {
    if (vessel) {
      const url: string =
        this.apiHelper.getServiceName() +
        this.getResource() +
        '/boatyard/partial/' +
        vesselId;
      const method = 'PUT';
      const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

      return this.apiHelper.apiCall<Vessel>(url, method, null, vessel, headers);
    }
  }

  transferVessel(vesselId: number, ownerId: number, existingOwnerId: number) {
    const url: string =
      this.apiHelper.getServiceName() +
      this.getResource() +
      '/transfer/' +
      vesselId;
    const method = 'POST';
    const headers: HttpHeaders = this.apiHelper.getDefaultHeader();

    return this.apiHelper.apiCall<Vessel>(
      url,
      method,
      null,
      { ownerId, existingOwnerId },
      headers,
    );
  }
}
